// Détail CHIFFRÉ d'une violation de règle, pour l'affichage.
//
// PROBLÈME
// Le tooltip disait « Règle de consistance violée » et s'arrêtait là. Le
// trader ne pouvait ni vérifier, ni comprendre : quel jour ? quel pourcentage
// atteint ? contre quel seuil ? Sur un compte à 3 trades, le message ressemble
// à un bug alors que la règle est réellement franchie (34 % contre un seuil à
// 30 %). Sans le chiffre, impossible de faire la différence.
//
// Deux défauts distincts vivaient dans ce mapping, recopié dans trois écrans
// (TradingAccountsPage, AccountsTableWidget, DayDetailModal) :
//   1. aucun chiffre, donc aucun moyen de vérifier ;
//   2. les libellés étaient aplatis SANS filtrer par gravité, si bien qu'un
//      jour en simple approche pouvait afficher « violée » sous l'icône rouge
//      d'un autre jour.
//
// Module PUR : aucun import Wasp/Prisma, testable isolément.

import type { ViolationType } from "./propFirmEngine";

export type ViolationSeverity = "violation" | "warning";

/** Entrée de `ruleViolations`, telle que le serveur la sérialise. */
export interface DayViolationLike {
  date: string;
  types: ViolationType[];
  dailyPnl: number;
  maxDailyLossAllowed: number | null;
  consistencyRulePercent: number | null;
  /** Base de comparaison de la consistance. Absent des payloads d'avant ce correctif. */
  consistencyBase?: number | null;
  /** Part du jour dans cette base, en %. Absent des payloads d'avant ce correctif. */
  daySharePercent?: number | null;
  totalProfit: number | null;
}

export interface ViolationDetail {
  date: string;
  type: ViolationType;
  severity: ViolationSeverity;
  dailyPnl: number;
  /** Consistance : part du jour, en %. Null si le serveur ne l'a pas fournie. */
  daySharePercent: number | null;
  /** Consistance : seuil du palier, en %. */
  thresholdPercent: number | null;
  /** Consistance : base de comparaison (profit réalisé en funded, profit target en challenge). */
  consistencyBase: number | null;
  /** Perte journalière : montant perdu, positif. */
  dailyLoss: number | null;
  /** Perte journalière : plafond autorisé. */
  maxDailyLossAllowed: number | null;
}

function severityOf(type: ViolationType): ViolationSeverity {
  return type.endsWith("_violation") ? "violation" : "warning";
}

/**
 * Aplatit la carte `ruleViolations` en une liste d'entrées CHIFFRÉES, une par
 * (jour × type).
 *
 * Chaque entrée porte sa propre gravité : l'UI peut donc afficher l'icône rouge
 * sur la seule présence d'une `violation` tout en libellant correctement les
 * `warning` qui l'accompagnent, au lieu de les confondre.
 *
 * Les violations sont triées AVANT les avertissements — dans un tooltip borné
 * en hauteur, c'est l'information grave qui doit rester visible.
 *
 * RÉTROCOMPATIBILITÉ : `daySharePercent` et `consistencyBase` sont optionnels.
 * Un client à jour parlant à un serveur qui ne les envoie pas encore retombe
 * sur `null` et affiche le libellé sans chiffre, au lieu d'un « NaN% ».
 */
export function buildViolationDetails(
  entries: DayViolationLike[],
): ViolationDetail[] {
  const details: ViolationDetail[] = [];

  for (const entry of entries) {
    if (!entry?.types?.length) continue;

    for (const type of entry.types) {
      const isConsistency = type.startsWith("consistency");

      details.push({
        date: entry.date,
        type,
        severity: severityOf(type),
        dailyPnl: entry.dailyPnl,
        daySharePercent: isConsistency
          ? typeof entry.daySharePercent === "number" && Number.isFinite(entry.daySharePercent)
            ? entry.daySharePercent
            : null
          : null,
        thresholdPercent: isConsistency ? entry.consistencyRulePercent : null,
        consistencyBase: isConsistency
          ? typeof entry.consistencyBase === "number" && Number.isFinite(entry.consistencyBase)
            ? entry.consistencyBase
            : null
          : null,
        dailyLoss: !isConsistency && entry.dailyPnl < 0 ? Math.abs(entry.dailyPnl) : null,
        maxDailyLossAllowed: !isConsistency ? entry.maxDailyLossAllowed : null,
      });
    }
  }

  return details.sort((a, b) => {
    if (a.severity !== b.severity) return a.severity === "violation" ? -1 : 1;
    return a.date.localeCompare(b.date);
  });
}
